import { datesInclusive, parseIsoDate } from "./date.ts";
import { DomainError, type IsoDate, type MatrixDocument } from "./model.ts";
import { firstRecordedDate, scoreDay, type ScoreOptions } from "./scoring.ts";

export interface AccumulationDay {
  readonly date: IsoDate;
  readonly score: number | null;
  readonly total: number;
}

export interface AccumulationResult {
  readonly anchor: IsoDate | null;
  readonly through: IsoDate;
  readonly total: number;
  readonly scoredDays: number;
  readonly series: readonly AccumulationDay[];
}

export interface AnchorResetPreview {
  readonly resetId: string;
  readonly currentAnchor: IsoDate;
  readonly proposedAnchor: IsoDate;
  readonly through: IsoDate;
  readonly currentTotal: number;
  readonly proposedTotal: number;
  readonly delta: number;
  readonly affectedRecordedDates: readonly IsoDate[];
  readonly confirmationText: string;
}

export interface AnchorResetAudit {
  readonly resetId: string;
  readonly previousAnchor: IsoDate;
  readonly nextAnchor: IsoDate;
  readonly impactDelta: number;
  readonly confirmedAt: string;
}

export function accumulation(
  document: MatrixDocument,
  through: IsoDate,
  anchor?: IsoDate | null,
  options: ScoreOptions = {},
): AccumulationResult {
  parseIsoDate(through);
  const resolvedAnchor = anchor === undefined ? firstRecordedDate(document, through) : anchor;
  if (resolvedAnchor === null) return { anchor: null, through, total: 0, scoredDays: 0, series: [] };
  parseIsoDate(resolvedAnchor);
  if (resolvedAnchor > through) throw new DomainError("ANCHOR_AFTER_THROUGH", "accumulation anchor cannot be after the end date");
  let total = 0;
  let scoredDays = 0;
  const series = datesInclusive(resolvedAnchor, through).map((date) => {
    const score = scoreDay(document, date, { ...options, asOf: options.asOf ?? through }).score;
    if (score !== null) {
      total += score;
      scoredDays += 1;
    }
    return { date, score, total };
  });
  return { anchor: resolvedAnchor, through, total, scoredDays, series };
}

function fingerprint(parts: readonly string[]): string {
  let hash = 0x811c9dc5;
  for (const character of parts.join("\u001f")) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193);
  }
  return `anchor-reset-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function previewAnchorReset(
  document: MatrixDocument,
  currentAnchor: IsoDate,
  proposedAnchor: IsoDate,
  through: IsoDate,
  options: ScoreOptions = {},
): AnchorResetPreview {
  parseIsoDate(currentAnchor);
  parseIsoDate(proposedAnchor);
  parseIsoDate(through);
  const first = firstRecordedDate(document, through);
  if (first === null) throw new DomainError("NO_RECORDED_HISTORY", "an accumulation anchor requires recorded history");
  if (proposedAnchor < first) throw new DomainError("ANCHOR_BEFORE_HISTORY", `anchor cannot precede first recorded date ${first}`);
  if (proposedAnchor > through) throw new DomainError("ANCHOR_AFTER_THROUGH", "anchor cannot be after the end date");
  const current = accumulation(document, through, currentAnchor, options);
  const proposed = accumulation(document, through, proposedAnchor, options);
  const low = currentAnchor < proposedAnchor ? currentAnchor : proposedAnchor;
  const high = currentAnchor < proposedAnchor ? proposedAnchor : currentAnchor;
  const affectedRecordedDates = [...new Set(document.entries
    .map((entry) => entry.date)
    .filter((date) => date >= low && date < high))].sort();
  const resetId = fingerprint([
    document.ownerId,
    currentAnchor,
    proposedAnchor,
    through,
    current.total.toFixed(12),
    proposed.total.toFixed(12),
  ]);
  return {
    resetId,
    currentAnchor,
    proposedAnchor,
    through,
    currentTotal: current.total,
    proposedTotal: proposed.total,
    delta: proposed.total - current.total,
    affectedRecordedDates,
    confirmationText: `RESET ${currentAnchor} TO ${proposedAnchor}`,
  };
}

export function confirmAnchorReset(
  preview: AnchorResetPreview,
  suppliedResetId: string,
  suppliedConfirmationText: string,
  confirmedAt: string,
): AnchorResetAudit {
  if (suppliedResetId !== preview.resetId || suppliedConfirmationText !== preview.confirmationText) {
    throw new DomainError("ANCHOR_RESET_NOT_CONFIRMED", "anchor reset requires the exact preview id and confirmation text");
  }
  if (Number.isNaN(Date.parse(confirmedAt))) {
    throw new DomainError("INVALID_CONFIRMATION_TIME", "confirmedAt must be an ISO timestamp");
  }
  return {
    resetId: preview.resetId,
    previousAnchor: preview.currentAnchor,
    nextAnchor: preview.proposedAnchor,
    impactDelta: preview.delta,
    confirmedAt,
  };
}
